/* payrollPaytype.js -- commission ship 2b (2026-10-09): THE PAY TYPE OF A STEP-3 "HANDLED BY PAYROLL" ROW.
 *
 * Brian 10-09 -- N3 (a): the pay type is a STORED answer (gl_line_mappings.payroll_paytype), SUGGESTED from the line's name
 * (matchPayrollRole -> PAYROLL_ROLE_TO_PAYTYPE, the same matcher and map the rest of onboarding uses), shown marked
 * "suggested -- confirm or change", and overridable. Never a silent guess: a suggestion counts only once the operator accepts
 * it (Step 4a's page confirm, reused on Step 3) or picks a value. Q4 A / N4 (a): a stored 'commissions' answer will give that
 * (category, group) a Commissions line in ship 2c; nothing reads the answer in 2b, so nothing a tenant books moves.
 *
 * Seeding order for a row the grid shows as "Handled by Payroll": (i) the stored answer; (ii) else the name's suggestion,
 * marked; (iii) else blank and required. A row that is not "Handled by Payroll" carries no pay type, and the save sends NULL
 * for it (the server refuses a value there: payroll_paytype_not_payroll_row).
 */
import { PAYTYPES, PAYTYPE_CHOICE_LABELS, PAYTYPE_SORT_OFFSET, PAYROLL_ROLE_TO_PAYTYPE } from '../engines/payrollKeys';
import { matchPayrollRole } from './inferIsMeta';

/* The choices, in the order the payroll lines sit on the statement (PAYTYPE_SORT_OFFSET): Wages, Bonuses, Commissions,
 * Payroll taxes, Benefits. Derived from the owners -- no second list. */
export const PAYTYPE_CHOICES = [...PAYTYPES]
  .sort((a, b) => PAYTYPE_SORT_OFFSET[a] - PAYTYPE_SORT_OFFSET[b])
  .map(value => ({ value, label: PAYTYPE_CHOICE_LABELS[value] }));

export const isPayrollPaytype = v => typeof v === 'string' && PAYTYPES.includes(v);

/* The name's suggestion, or null when the name matches no payroll role. */
export function suggestPayrollPaytype(glName) {
  const role = matchPayrollRole(glName);
  return (role && PAYROLL_ROLE_TO_PAYTYPE[role]) || null;
}

/* Bring a Step-3 row's pay type in line with whether it shows "Handled by Payroll" (row.payrollHandled). A row that does not
 * carries none; a row that does keeps the value it has (stored or chosen, with its mark as it is), else takes the suggestion,
 * marked. Called at load, on a per-row category pick and on a bulk assign -- the one rule for all three. */
export function withPayrollPaytype(row) {
  if (!row.payrollHandled) {
    return (row.payroll_paytype == null && !row.paytypeSuggested) ? row : { ...row, payroll_paytype: null, paytypeSuggested: false };
  }
  if (isPayrollPaytype(row.payroll_paytype)) return row;
  const s = suggestPayrollPaytype(row.gl_name);
  return { ...row, payroll_paytype: s, paytypeSuggested: !!s };
}

/* What the Step-3 save sends: the row's pay type when the row IS stored as "Handled by Payroll" (an IS Skip row with
 * handled_by 'payroll'), else NULL. The server ASSIGNS the column (never COALESCE), so this must be sent on every row. */
export function payrollPaytypeForSave(m) {
  const payrollRow = m.statement_type === 'IS' && m.is_category === 'Skip' && m.handled_by === 'payroll';
  return payrollRow && isPayrollPaytype(m.payroll_paytype) ? m.payroll_paytype : null;
}

/* A row showing "Handled by Payroll" whose pay type is not yet an answer: blank, or a suggestion the page confirm has not
 * accepted. The Step-3 gate, its banner and the save's refusal read this one predicate. */
export function unansweredPayrollPaytype(m, suggestionsAccepted) {
  if (!m.payrollHandled) return false;
  if (!isPayrollPaytype(m.payroll_paytype)) return true;
  return !!m.paytypeSuggested && !suggestionsAccepted;
}
